/* eslint-disable react/prop-types */
"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { User } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { dashboardLinks } from "@/components/lib/SidebarLinks";
import { Skeleton } from "@/components/ui/skeleton";
import useAuth from "@/hooks/useAuth";
import { fetchEmployeeAnalysis } from "@/redux/slice/dashboard/dashboard-slice";

const ROLE_COLORS = [
  "#38B2AC",
  "#007BFF",
  "#5850EC",
  "#AF52DE",
  "#33AADD",
  "#10B981",
  "#F59E0B",
  "#EF4444",
];

const RootNode = ({ name, total, loading }) => (
  <div className="w-[160px] h-[120px] bg-[#22C55E] rounded-xl flex flex-col items-center justify-center text-white shadow-xl">
    {loading ? (
      <>
        <Skeleton className="h-4 w-20 mb-2 bg-white/20" />
        <Skeleton className="h-8 w-16 bg-white/20" />
      </>
    ) : (
      <>
        <span className="text-sm font-medium mb-2">{name}</span>
        <div className="flex items-center gap-2">
          <User size={22} />
          <span className="text-2xl font-bold">
            {Number(total || 0).toLocaleString()}
          </span>
        </div>
      </>
    )}
  </div>
);

const RoleNode = ({ title, count, color, loading }) => (
  <div
    className="w-full max-w-[180px] h-[112px] rounded-lg flex flex-col items-center justify-center text-white shadow-lg"
    style={{ backgroundColor: color }}
  >
    {loading ? (
      <>
        <Skeleton className="h-4 w-24 bg-white/20" />
        <Skeleton className="h-6 w-12 mt-2 bg-white/20" />
      </>
    ) : (
      <>
        <span className="text-sm font-medium opacity-90 text-center leading-tight min-h-[34px] px-2 flex items-center justify-center">
          {title}
        </span>
        <div className="flex items-center gap-2 mt-1">
          <User size={18} />
          <span className="text-2xl font-bold">
            {Number(count || 0).toLocaleString()}
          </span>
        </div>
      </>
    )}
  </div>
);

export default function StaffReports() {
  const dispatch = useDispatch();
  const { user } = useAuth();

  const employeeAnalysis = useSelector(
    (state) => state.dashboard?.employeeAnalysis,
  );
  const employeeAnalysisLoading = useSelector(
    (state) => state.dashboard?.employeeAnalysisLoading,
  );

  const analytics = employeeAnalysis?.analytics || [];

  const [activeRestaurantIndex, setActiveRestaurantIndex] = useState(0);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (hasLoadedRef.current || !user?.restaurant_id) return;

    dispatch(
      fetchEmployeeAnalysis({
        restaurant_id: user.restaurant_id,
      }),
    );

    hasLoadedRef.current = true;
  }, [dispatch, user?.restaurant_id]);

  useEffect(() => {
    if (!analytics.length) return;
    if (activeRestaurantIndex > analytics.length - 1) {
      setActiveRestaurantIndex(0);
    }
  }, [activeRestaurantIndex, analytics.length]);

  const activeRestaurant = analytics[activeRestaurantIndex] || null;

  const roleNodes = useMemo(() => {
    const roles = activeRestaurant?.roles || [];
    return roles.map((role, index) => ({
      title: role?.role_name || "Role",
      count: role?.employee_count || 0,
      color: ROLE_COLORS[index % ROLE_COLORS.length],
      index,
    }));
  }, [activeRestaurant]);

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={dashboardLinks} />}
      pageName={{ title: "Staff Reports" }}
      paths={[
        { name: "Dashboard", path: "/dashboard/overview" },
        { name: "Reports & Analytics", path: "/dashboard/revenue-reports" },
        { name: "Staff", path: "/dashboard/staff-reports" },
      ]}
    >
      <div className="container mx-auto p-6 flex flex-col min-h-screen text-white font-sans overflow-hidden">


        <div className="relative w-full mt-2 bg-[#111111] border border-white/5 rounded-2xl p-8 md:p-12 flex flex-col items-center min-h-[620px]">
          <div className="relative z-10 mb-14">
            <RootNode
              name={activeRestaurant?.restaurant_name || "Restaurant"}
              total={activeRestaurant?.total_employees || 0}
              loading={employeeAnalysisLoading && !activeRestaurant}
            />
          </div>

          {!employeeAnalysisLoading && !roleNodes.length ? (
            <div className="text-gray-400 text-sm mt-10">
              No role data found
            </div>
          ) : (
            <>
              <div className="w-full mt-2 mb-4 relative z-10 flex flex-col items-center">
                <div className="h-8 w-px bg-[#22C55E]" />
                <div className="hidden md:block h-px w-full max-w-5xl bg-[#22C55E]/60" />
              </div>

              <div className="w-full relative z-10 px-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5 justify-items-center">
                  {(employeeAnalysisLoading && !roleNodes.length
                    ? Array.from({ length: 5 }).map((_, idx) => ({
                        title: "Role",
                        count: 0,
                        color: ROLE_COLORS[idx % ROLE_COLORS.length],
                        idx,
                      }))
                    : roleNodes
                  ).map((node) => (
                    <div
                      key={node.idx}
                      className="w-full flex flex-col items-center"
                    >
                      <div
                        className="h-6 w-px"
                        style={{ backgroundColor: node.color }}
                      />
                      <div
                        className="w-2 h-2 rounded-full -mt-0.5 mb-2"
                        style={{ backgroundColor: node.color }}
                      />
                      <RoleNode
                        title={node.title}
                        count={node.count}
                        color={node.color}
                        loading={employeeAnalysisLoading && !activeRestaurant}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout2>
  );
}
